import React from "react";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";

interface CardData {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface InformationalCardsSectionProps {
  heading: string;
  subheading?: string;
  bulletItems?: string[];
  imageSrc?: string;
  imageAlt?: string;
  imagePosition?: "left" | "right";
  cards: CardData[];
}

const InformationalCardsSection: React.FC<InformationalCardsSectionProps> = ({
  heading,
  subheading,
  bulletItems,
  imageSrc,
  imageAlt = "Informational image",
  imagePosition = "left",
  cards,
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#0b0f17",
        py: { xs: 4, md: 8 },
        px: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Box textAlign="center" mb={2}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#a78bfa",
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
            }}
          >
            {heading}
          </Typography>
        </Box>

        {subheading && (
          <Box textAlign="center" maxWidth="800px" mx="auto" mb={2}>
            <Typography variant="body1" sx={{ color: "#94a3b8" }}>
              {subheading}
            </Typography>
          </Box>
        )}

        {bulletItems && bulletItems.length > 0 && (
          <Box
            component="ul"
            sx={{
              listStyle: "none",
              p: 0,
              m: 0,
              mb: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            {bulletItems.map((item) => (
              <Box
                component="li"
                key={item}
                sx={{
                  color: "#64748b",
                  fontSize: "0.875rem",
                  textAlign: "center",
                  lineHeight: 1.5,
                }}
              >
                {item}
              </Box>
            ))}
          </Box>
        )}

        <Grid
          container
          spacing={4}
          alignItems="center"
          direction={imagePosition === "left" ? "row" : "row-reverse"}
        >
          <Grid size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {imageSrc ? (
                <Box
                  component="img"
                  src={imageSrc}
                  alt={imageAlt}
                  sx={{
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "16px",
                    border: "1px solid rgba(51, 65, 85, 0.8)",
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No Image Provided
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {cards.map((card, index) => (
                <Box
                  key={index}
                  sx={{
                    backgroundColor: "rgba(15, 23, 42, 0.9)",
                    borderRadius: 2,
                    border: "1px solid rgba(51, 65, 85, 0.8)",
                    p: 3,
                    transition: "border-color 0.2s",
                    "&:hover": {
                      borderColor: "rgba(139, 92, 246, 0.45)",
                    },
                  }}
                >
                  <Box mb={2} sx={{ color: "#a78bfa" }}>
                    {card.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, marginBottom: "0.5rem", color: "#fff" }}
                  >
                    {card.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    {card.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default InformationalCardsSection;
